import { Car, AlertTriangle } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  RouteResponse,
  isRouteResult,
  isRouteError,
} from '../dayplanning.types';

interface RouteSegmentProps {
  route: RouteResponse | null;
  routeOption: 'direct' | 'via_hq' | null;
  onRouteOptionChange: (option: 'direct' | 'via_hq') => void;
  isLoading?: boolean;
}

export function RouteSegment({
  route,
  routeOption,
  onRouteOptionChange,
  isLoading,
}: RouteSegmentProps) {
  const isSuccess = isRouteResult(route);
  const isError = isRouteError(route);

  return (
    <div className="text-muted-foreground flex items-center gap-3 px-4 py-2 text-sm">
      <div className="flex flex-1 items-center gap-3">
        <Car className="h-4 w-4 shrink-0" />
        {isLoading ? (
          <span className="text-muted-foreground/50" aria-live="polite">
            Calculating route...
          </span>
        ) : isSuccess && route ? (
          <>
            <span>{(route.distance_km || 0).toFixed(1) || '-'} km</span>
            <span>·</span>
            <span>{Math.round(route.duration_minutes || 0)} min</span>
          </>
        ) : isError && route ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1 text-red-600 dark:text-red-500">
                  <AlertTriangle className="h-3 w-3" />
                  <span>Route failed</span>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>{route.error}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-muted-foreground/50">No route</span>
        )}
      </div>

      <Select
        value={routeOption ?? undefined}
        defaultValue="direct"
        onValueChange={(v) => onRouteOptionChange(v as 'direct' | 'via_hq')}
      >
        <SelectTrigger className="h-7 w-28 text-xs" aria-label="Route option">
          <SelectValue placeholder="Direct" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="direct">Direct</SelectItem>
          <SelectItem value="via_hq">Via HQ</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
